/**
 * Type definitions for the Trade Model Analysis application
 */

/**
 * A numeric N-dimensional array as returned in model params/sol.
 * Covers scalars and 1-D through 3-D numeric arrays.
 */
export type NDArray = number | number[] | number[][] | number[][][];

export type Representation = 'baseline' | 'counterfactual' | 'hats';
export type ModelValues = Record<string, NDArray | null>;
export interface VariableMetadata {
  symbol: string;
  meaning: string;
  shape: string[];
  axes: { name: string; kind: 'country' | 'sector' }[];
  units: string;
  calibration_url: string | null;
}
export interface BaseModel {
  N: number;
  J: number;
  country_list: string[];
  sector_list: string[];
  params: ModelValues;
  sol: Record<Representation, ModelValues>;
  variables: Record<string, VariableMetadata>;
  data_metadata?: { dataset?: string; year?: number; monetary_units?: string; calibration?: string };
}

export interface TariffRule {
  importer: string;  // country name or "All"
  exporter: string;
  sector: string;
  rate: number;
  mode: 'Increment' | 'Set Level';
}

export interface TariffConfiguration {
  importers: string[];
  exporters: string[];
  sectors: string[];
  tariff_data: {
    [key: string]: {
      rate: number;
      mode: 'Increment' | 'Set Level';
    };
  };
  /** Frontend-only metadata for edit round-trip (backend ignores) */
  tariffMode?: 'uniform' | 'custom';
  customRules?: TariffRule[];
}

export interface KappaConfig {
  eta?: number;
  eta_prime?: number;
  kappa_S: Record<string, number>;
  kappa_D: Record<string, number>;
}

/** True when a tariff config actually carries at least one tariff rule. */
export function hasTariffShock(config: TariffConfiguration | undefined | null): boolean {
  return !!config && Object.keys(config.tariff_data).length > 0;
}

/** True when a kappa config carries at least one non-default multiplier. */
export function hasKappaShock(config: KappaConfig | undefined | null): boolean {
  if (!config) return false;
  return Object.keys(config.kappa_S).length > 0 || Object.keys(config.kappa_D).length > 0 || config.eta_prime !== undefined;
}

/**
 * A scenario represents a single counterfactual analysis configuration.
 * Shocks are composable: a scenario may carry a tariff shock, a κ shock, or
 * both applied together in a single solve.
 */
export interface Scenario {
  framework: string;
  dataset: string;
  id: string;
  name: string;
  /** Always present; may be EMPTY_TARIFF_CONFIG when the tariff section is disabled. */
  tariffConfig: TariffConfiguration;
  /** Present only when the κ shock section is enabled (Furusawa2023-only). */
  kappaConfig?: KappaConfig;
  result: BaseModel | null;
  status: 'pending' | 'solving' | 'solved' | 'error';
  error?: string;
  createdAt: Date;
}

/** Empty tariff configuration placeholder used when the tariff section is disabled. */
export const EMPTY_TARIFF_CONFIG: TariffConfiguration = {
  importers: [],
  exporters: [],
  sectors: [],
  tariff_data: {},
};

